import {
  deleteFridge,
  deleteSensor,
  useActiveSensorAlerts,
  useFridges,
  useSensors,
  type SensorAlertCategory,
  type SensorData,
} from '@gozoki/api'
import { Button, MenuItem, Paper, Select, Tab, Tabs, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { PlusSquare } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import ActiveAlertsDrawer from '../../../components/routes/sensors/ActiveAlertsDrawer'
import {
  alertingSensorIdSet,
  filterFridgesInAlert,
  filterSensorsInAlert,
} from '../../../components/routes/sensors/sensorAlertFilter'
import { AlertSettingsPanel } from '../../../components/routes/sensors/AlertSettingsPanel'
import { useSensorAlerts } from '../../../utils/websockets/useSensorAlerts'
import z from 'zod/v3'
import { AuthorizedButtonLink, withRoutePermissions } from '~/utils/rbac'
import { ConfirmButton } from '../../../components/communication/ConfirmButton'
import Page from '../../../components/Page'
import FridgeGrid from '../../../components/routes/sensors/FridgeGrid'
import SensorChart from '../../../components/routes/sensors/SensorChart'
import SensorGrid from '../../../components/routes/sensors/SensorGrid'
import SensorStatusCards from '../../../components/routes/sensors/SensorStatusCards'
import { StoresPicker } from '../../../utils/dashboard/storesPicker'
import { useRights } from '../../../utils/hooks'
import { fonts } from '../../../utils/theme/fonts.theme'
import { ButtonOverrides } from '../../../utils/theme/overrides.theme'

const tabs = ['sensors', 'fridges', 'settings'] as const

// Valeur sentinelle du sélecteur : « En alerte » (≠ tout id de boutique, positifs).
const ALERTING_STORE_VALUE = -1
// Valeur « Toutes les boutiques » injectée par StoresPicker via allowAllStores.
const ALL_STORES_VALUE = 0
// Référence stable pour éviter de recalculer storeSelectOptions à chaque rendu.
const SENSOR_EXTRA_OPTIONS = [{ label: 'En alerte', value: ALERTING_STORE_VALUE }]

type CategoryFilter = 'ALL' | SensorAlertCategory

export function categoryOf(sensor: SensorData): SensorAlertCategory {
  if (sensor.type === 'ROOM') return 'AMBIANT'
  if (sensor.fridge?.type === 'COLD_ROOM') return 'CHAMBRE_FROIDE'
  return 'FRIGO'
}

export const Route = createFileRoute('/_authenticated/sensors/')({
  staticData: {
    permission: ['read', 'sensors'],
  },
  component: SensorsPage,
  validateSearch: z.object({
    tab: z.enum(tabs).optional(),
    categoryFilter: z
      .enum(['ALL', 'AMBIANT', 'FRIGO', 'CHAMBRE_FROIDE'] as const)
      .optional()
      .default('ALL'),
  }),
})
withRoutePermissions(Route)

function SensorsPage() {
  const navigate = Route.useNavigate()
  const { tab = 'sensors', categoryFilter = 'ALL' } = Route.useSearch()

  const queryClient = useQueryClient()

  const [currentStore, setCurrentStore] = useState<number>(ALERTING_STORE_VALUE)
  const isAlertMode = currentStore === ALERTING_STORE_VALUE
  const effectiveStoreId = isAlertMode || currentStore === ALL_STORES_VALUE ? null : currentStore
  const [selectedSensorIds, setSelectedSensorIds] = useState<number[]>([])
  const [selectedFridgeIds, setSelectedFridgeIds] = useState<number[]>([])
  const [currentPageSensorIds, setCurrentPageSensorIds] = useState<number[]>([])
  const [alertsDrawerOpen, setAlertsDrawerOpen] = useState(false)

  useSensorAlerts()

  const { data: sensors, isLoading } = useSensors(effectiveStoreId)
  const { data: fridges, isLoading: isLoadingFridges } = useFridges(effectiveStoreId)
  const { data: activeAlerts } = useActiveSensorAlerts()
  const isCpiGlobalOrSuperAdmin = useRights(['CPI_GLOBAL', 'SUPER_ADMIN'])

  const alertingSensorIds = useMemo(() => alertingSensorIdSet(activeAlerts), [activeAlerts])

  // En mode alerte : ne garder que les équipements en alerte moteur (toutes boutiques).
  const sensorsInScope = useMemo(() => {
    if (!sensors) return undefined
    return isAlertMode ? filterSensorsInAlert(sensors, alertingSensorIds) : sensors
  }, [sensors, isAlertMode, alertingSensorIds])

  const fridgesInScope = useMemo(() => {
    if (!fridges) return undefined
    if (!isAlertMode) return fridges
    // Ne pas filtrer contre un set de capteurs vide pendant que `sensors` charge :
    // `useFridges` garde les données précédentes (keepPreviousData) mais pas
    // `useSensors`, donc on resterait en chargement plutôt que d'afficher un faux
    // « Aucun réfrigérateur en alerte » avec des frigos périmés.
    if (!sensors) return undefined
    return filterFridgesInAlert(fridges, sensors, alertingSensorIds)
  }, [fridges, sensors, isAlertMode, alertingSensorIds])

  // Réinitialiser les sélections et la page courante au changement de boutique / de
  // mode : évite une cible de suppression ou une série de graphe héritées d'une autre
  // boutique (la grille MUI ne purge pas la sélection vers les nouvelles lignes).
  useEffect(() => {
    setSelectedSensorIds([])
    setSelectedFridgeIds([])
    setCurrentPageSensorIds([])
  }, [currentStore])

  // Get sensor IDs from selected fridges
  const sensorIdsFromFridges = useMemo(() => {
    if (selectedFridgeIds.length === 0 || !fridges || !sensors) {
      return []
    }

    // Get fridge references from selected fridge IDs
    const selectedFridgeReferences = new Set(
      fridges
        .filter((fridge) => selectedFridgeIds.includes(fridge.id))
        .map((fridge) => fridge.reference),
    )

    // Get sensor IDs that belong to selected fridges
    return sensors
      .filter((sensor) => sensor.fridge && selectedFridgeReferences.has(sensor.fridge.reference))
      .map((sensor) => sensor.id)
  }, [selectedFridgeIds, fridges, sensors])

  // Combine selected sensor IDs with sensors from selected fridges
  const allSelectedSensorIds = useMemo(() => {
    const combined = [...selectedSensorIds, ...sensorIdsFromFridges]
    // Remove duplicates
    return [...new Set(combined)]
  }, [selectedSensorIds, sensorIdsFromFridges])

  // Filtre catégorie appliqué par-dessus le filtre alerte.
  const filteredSensors = useMemo(() => {
    if (!sensorsInScope) return undefined
    if (categoryFilter === 'ALL') return sensorsInScope
    return sensorsInScope.filter((sensor) => categoryOf(sensor) === categoryFilter)
  }, [sensorsInScope, categoryFilter])

  const handleChange = (newValue: number) => {
    const newTab = tabs.at(newValue) ?? 'sensors'
    setSelectedSensorIds([])
    setSelectedFridgeIds([])
    navigate({ search: { tab: newTab, categoryFilter: 'ALL' } })
  }

  const handleCategoryFilterChange = (value: CategoryFilter) => {
    setSelectedSensorIds([])
    navigate({ search: (prev) => ({ ...prev, categoryFilter: value }) })
  }

  // Delete mutations
  const deleteSensorMut = useMutation({
    mutationFn: deleteSensor,
    onSuccess: () => {
      // Invalidate sensors queries to refetch the list
      queryClient.invalidateQueries({ queryKey: ['sensors'] })
      setSelectedSensorIds([])
    },
  })

  const deleteFridgeMut = useMutation({
    mutationFn: deleteFridge,
    onSuccess: () => {
      // Invalidate fridges query to refetch the list
      queryClient.invalidateQueries({ queryKey: ['fridges'] })
      setSelectedFridgeIds([])
    },
  })

  return (
    <Page>
      <div style={{ display: 'flex', paddingBottom: 10, paddingLeft: 10 }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Équipements de Réfrigération
        </Typography>
        {tab === 'sensors' && selectedSensorIds.length === 1 && (
          <>
            <ConfirmButton
              buttonText="Supprimer ce capteur"
              buttonProps={{
                disabled: !isCpiGlobalOrSuperAdmin || deleteSensorMut.isPending,
                sx: {
                  marginRight: '16px',
                  height: '42px',
                  ...ButtonOverrides.redOutlined,
                },
                variant: 'outlined',
              }}
              description="Etes-vous sûrs de vouloir supprimer ce capteur ? Cette action est irréversible et supprimera toutes les données associées à ce capteur."
              onConfirm={() => deleteSensorMut.mutateAsync(selectedSensorIds[0])}
              title="Supprimer ce capteur ?"
              confirmButton={
                deleteSensorMut.isPending
                  ? {
                      loading: true,
                      text: 'Suppression en cours     ',
                    }
                  : { text: 'Supprimer' }
              }
            />
            <Button
              variant="contained"
              sx={{ marginRight: '16px' }}
              disabled={!isCpiGlobalOrSuperAdmin}
              onClick={() =>
                navigate({
                  search: { sensorId: selectedSensorIds[0] },
                  to: '/sensors/new',
                })
              }
            >
              Modifier ce capteur
            </Button>
          </>
        )}
        {tab === 'fridges' && selectedFridgeIds.length === 1 && (
          <>
            <ConfirmButton
              buttonText="Supprimer ce réfrigérateur"
              buttonProps={{
                disabled: !isCpiGlobalOrSuperAdmin || deleteFridgeMut.isPending,
                sx: {
                  marginRight: '16px',
                  height: '42px',
                  ...ButtonOverrides.redOutlined,
                },
                variant: 'outlined',
              }}
              description="Etes-vous sûrs de vouloir supprimer ce réfrigérateur ? Cette action est irréversible et supprimera toutes les données associées à ce réfrigérateur."
              onConfirm={() => deleteFridgeMut.mutateAsync(selectedFridgeIds[0])}
              title="Supprimer ce réfrigérateur ?"
              confirmButton={
                deleteFridgeMut.isPending
                  ? {
                      loading: true,
                      text: 'Suppression en cours     ',
                    }
                  : { text: 'Supprimer' }
              }
            />
            <Button
              variant="contained"
              sx={{ marginRight: '16px' }}
              disabled={!isCpiGlobalOrSuperAdmin}
              onClick={() =>
                navigate({
                  search: { fridgeId: selectedFridgeIds[0] },
                  to: '/sensors/fridge/new',
                })
              }
            >
              Modifier ce réfrigérateur
            </Button>
          </>
        )}
        {tab === 'sensors' && (
          <AuthorizedButtonLink
            variant="contained"
            sx={{ marginRight: '16px' }}
            disabled={!isCpiGlobalOrSuperAdmin}
            to="/sensors/new"
          >
            <PlusSquare />
            Ajouter un capteur
          </AuthorizedButtonLink>
        )}
        {tab === 'fridges' && (
          <AuthorizedButtonLink
            variant="contained"
            sx={{ marginRight: '16px' }}
            disabled={!isCpiGlobalOrSuperAdmin}
            to="/sensors/fridge/new"
          >
            <PlusSquare />
            Ajouter un réfrigérateur
          </AuthorizedButtonLink>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <StoresPicker
          setUniqueStore={setCurrentStore}
          extraOptions={SENSOR_EXTRA_OPTIONS}
          onlyBoutiques
          allowAllStores
          defaultStore={ALERTING_STORE_VALUE}
        />
      </div>
      <SensorStatusCards
        sensors={sensors}
        activeAlertSensorIds={alertingSensorIds}
        onAlertsClick={() => setAlertsDrawerOpen(true)}
      />
      <ActiveAlertsDrawer open={alertsDrawerOpen} onClose={() => setAlertsDrawerOpen(false)} />
      <Tabs
        value={tabs.indexOf(tab)}
        onChange={(_, newValue) => handleChange(newValue)}
        sx={{ marginBottom: 1 }}
      >
        <Tab label="Capteurs" />
        <Tab label="Réfrigérateurs" />
        <Tab label="Réglages" />
      </Tabs>
      {tab === 'settings' ? (
        <AlertSettingsPanel />
      ) : (
        <>
          <Paper
            sx={{
              marginTop: '16px',
            }}
          >
            {tab === 'sensors' && (
              <>
                <div style={{ padding: '12px 16px 0' }}>
                  <Select
                    size="small"
                    value={categoryFilter}
                    onChange={(e) => handleCategoryFilterChange(e.target.value as CategoryFilter)}
                    displayEmpty
                    sx={{ minWidth: 200 }}
                  >
                    <MenuItem value="ALL">Tous les équipements</MenuItem>
                    <MenuItem value="AMBIANT">Ambiant</MenuItem>
                    <MenuItem value="FRIGO">Frigo</MenuItem>
                    <MenuItem value="CHAMBRE_FROIDE">Chambre froide</MenuItem>
                  </Select>
                </div>
                {isAlertMode &&
                activeAlerts !== undefined &&
                filteredSensors &&
                filteredSensors.length === 0 ? (
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    align="center"
                    sx={{ padding: 4 }}
                  >
                    Aucun capteur en alerte
                  </Typography>
                ) : (
                  <SensorGrid
                    data={filteredSensors}
                    loading={isLoading && !sensors}
                    onSelectionChange={setSelectedSensorIds}
                    onPageChange={setCurrentPageSensorIds}
                  />
                )}
              </>
            )}
            {tab === 'fridges' &&
              (isAlertMode &&
              activeAlerts !== undefined &&
              fridgesInScope &&
              fridgesInScope.length === 0 ? (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  align="center"
                  sx={{ padding: 4 }}
                >
                  Aucun réfrigérateur en alerte
                </Typography>
              ) : (
                <FridgeGrid
                  data={fridgesInScope}
                  loading={isLoadingFridges && !fridges}
                  onSelectionChange={setSelectedFridgeIds}
                />
              ))}
          </Paper>
          <SensorChart
            selectedSensorIds={allSelectedSensorIds}
            isFridgeSelected={selectedFridgeIds.length > 0}
            storeId={effectiveStoreId}
            sensors={sensors}
            currentPageSensorIds={currentPageSensorIds}
          />
          <div style={{ margin: 10 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              {tab === 'sensors'
                ? 'Sélectionnez des capteurs dans le tableau pour afficher leur historique'
                : "Sélectionnez des réfrigérateurs dans le tableau pour afficher l'historique de leurs capteurs"}
            </Typography>
          </div>
        </>
      )}
    </Page>
  )
}
